import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMemo, useState } from "react";

import { StartTrackingStatusPill } from "../StartTracking/StartTrackingStatusChip";
import {
  alignForPercent,
  computeStartTrackingMetrics,
} from "../StartTracking/startTrackingMetrics";
import GoalsDataDisclaimer from "../GoalsDataDisclaimer";

const BAR_HEIGHT = 12;
const TRACK_BG = "#E8EAED";
const PROGRESS_FILL = "#00A63E";
const PROGRESS_FILL_NOT_ON_TRACK = "#e74c3c";
const BENCHMARK_LINE = "#374151";

const labelCaptionSx = {
  color: "#5F6368",
  fontSize: 11,
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const valueCaptionSx = {
  color: "#1F2937",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const BAR_PAD_Y = 32; // matches StartTracking-style py:4 so markers fit

/** Same copy as StartTrackingCard marker tooltips. */
const TOOLTIP_BENCHMARK =
  "Expected cumulative progress toward the quarterly goal as determined by days elapsed versus total days in the quarter.";
const TOOLTIP_GOAL = "The total quarterly goal value for your region.";
const TOOLTIP_PROGRESS =
  "The total referrals count for your region in the current quarter.";

function Triangle({ direction = "down", sx = {} }) {
  const isUp = direction === "up";
  return (
    <Box
      aria-hidden
      sx={{
        width: 0,
        height: 0,
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        ...(isUp
          ? { borderBottom: "8px solid #374151" }
          : { borderTop: "8px solid #374151" }),
        ...sx,
      }}
    />
  );
}

/**
 * Label anchored at a % along the bar; horizontal alignment shifts near edges
 * (same idea as StartTrackingCard markers).
 */
function SummaryBarMarker({
  id,
  label,
  valueText,
  tooltipText = "",
  tooltipAriaLabel = "More information",
  leftPercent,
  position = "above",
  anchor = "percent",
  align = "center",
  hoveredId,
  setHoveredId,
}) {
  const isAbove = position === "above";
  const isHovered = hoveredId === id;

  const left = anchor === "fillEnd" ? "100%" : `${leftPercent}%`;

  const labelTransform =
    align === "left"
      ? "translateX(0)"
      : align === "right"
        ? "translateX(-100%)"
        : "translateX(-50%)";

  const triangleTop = isAbove ? -10 : 22;
  const labelTop = isAbove ? -38 : 31;
  const hitboxTop = isAbove ? -58 : 18;
  const hitboxHeight = 64;

  return (
    <Box
      sx={{
        position: "absolute",
        left,
        top: 0,
        pointerEvents: "auto",
        zIndex: isHovered ? 9 : 2,
      }}
      onMouseEnter={() => setHoveredId(id)}
      onMouseLeave={() => setHoveredId(null)}
    >
      <Box
        sx={{
          position: "absolute",
          top: hitboxTop,
          left: 0,
          transform: "translateX(-50%)",
          width: 200,
          height: hitboxHeight,
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: triangleTop,
          left: 0,
          transform: "translateX(-50%)",
          pointerEvents: "none",
        }}
      >
        <Triangle direction={isAbove ? "down" : "up"} />
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: labelTop,
          left: 0,
          transform: labelTransform,
          whiteSpace: "nowrap",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          alignItems:
            align === "left"
              ? "flex-start"
              : align === "right"
                ? "flex-end"
                : "center",
          px: 1,
          py: 0.5,
          borderRadius: "8px",
          bgcolor: "rgba(255,255,255,0.92)",
          boxShadow: isHovered
            ? "0 10px 22px rgba(0,0,0,0.16)"
            : "0 2px 8px rgba(0,0,0,0.08)",
          transition: "box-shadow 150ms ease",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={
            align === "left"
              ? "flex-start"
              : align === "right"
                ? "flex-end"
                : "center"
          }
          spacing={0.5}
          sx={{ mx: align === "center" ? "auto" : undefined }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", gap: "2px" }}>
            <Typography variant="caption" sx={labelCaptionSx}>
              {label}
            </Typography>
            <Typography variant="caption" sx={valueCaptionSx}>
              {valueText}
            </Typography>
          </Box>
          {!!tooltipText && (
            <Tooltip
              title={tooltipText}
              placement="right"
              slotProps={{
                popper: {
                  modifiers: [
                    { name: "offset", options: { offset: [0, -10] } },
                  ],
                },
                tooltip: {
                  sx: {
                    bgcolor: "#FFFFFF",
                    color: "#2D2D2D",
                    border: "1px solid #E6E6E6",
                    boxShadow: "0px 1px 2px 0px #0000000D",
                  },
                },
              }}
            >
              <Box
                aria-label={tooltipAriaLabel}
                sx={{ lineHeight: 0, display: "inline-flex" }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 15, color: "#A0A0A0" }} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

function MyPerformanceSummarySkeleton({
  barHeight = BAR_HEIGHT,
  title = "My Performance",
}) {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: 16, color: "#111827" }}
          >
            {title}
          </Typography>
          <Skeleton
            variant="rounded"
            width={88}
            height={26}
            animation="wave"
            sx={{ borderRadius: 999 }}
          />
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems={{ xs: "stretch", md: "center" }}
        mb={2}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ py: 4, mx: { xs: 0, sm: "24px" } }}>
            <Skeleton
              variant="rounded"
              height={barHeight}
              animation="wave"
              sx={{ borderRadius: 999 }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Skeleton
            variant="rounded"
            width={200}
            height={44}
            animation="wave"
            sx={{ borderRadius: "4px" }}
          />
        </Box>
      </Stack>

      <Skeleton
        variant="text"
        width="40%"
        height={24}
        animation="wave"
        sx={{ mx: "auto", display: "block" }}
      />
    </>
  );
}

/**
 * Sales rep “My Performance” summary tile,
 * distinct layout aligned to the rep dashboard design.
 */
export default function MyPerformanceSummaryCard({
  title = "My Performance",
  buttonText = "Explore My Performance",
  status = "",
  goal = 0,
  progress = 0,
  todaysBenchmark = 0,
  onTrackStarts,
  estimatedHitGoal = -1,

  noGoalsData = false,
  goalsDataDisclaimerShow = false,
  isLoading = false,
}) {
  const { safeGoal, safeProgress, safeBenchmark, progressPct, benchmarkPct } =
    computeStartTrackingMetrics(goal, progress, todaysBenchmark);

  const isOnTrack = /^on track$/i.test(status);
  const progressBarFill = noGoalsData
    ? TRACK_BG
    : status && !isOnTrack
      ? PROGRESS_FILL_NOT_ON_TRACK
      : PROGRESS_FILL;

  const [hoveredId, setHoveredId] = useState(null);

  const benchAlign = useMemo(() => {
    if (benchmarkPct === 100) return "right";
    return alignForPercent(benchmarkPct);
  }, [benchmarkPct]);

  return (
    <Card
      variant="outlined"
      aria-busy={isLoading}
      sx={{
        borderRadius: "12px",
        borderColor: "#E0E0E0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        overflow: "visible",
        height: "100%",
        bgcolor: "#FFFFFF",
      }}
    >
      <CardContent sx={{ p: 3, overflow: "visible" }}>
        {isLoading ? (
          <MyPerformanceSummarySkeleton barHeight={BAR_HEIGHT} title={title} />
        ) : (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, fontSize: 16, color: "#111827" }}
                >
                  {title}
                </Typography>
                {!!status && !noGoalsData && (
                  <StartTrackingStatusPill status={status} />
                )}
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems={{ xs: "stretch", md: "center" }}
              mb={2}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    position: "relative",
                    mx: { xs: 0, sm: "24px" },
                    overflow: "visible",
                  }}
                >
                  <Box sx={{ py: 4 }}>
                    <Box
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={safeGoal}
                      aria-valuenow={safeProgress}
                      sx={{
                        height: BAR_HEIGHT,
                        borderRadius: "999px",
                        overflow: "hidden",
                        bgcolor: TRACK_BG,
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${progressPct}%`,
                          height: "100%",
                          borderRadius: "999px",
                          bgcolor: progressBarFill,
                          transition: "width 300ms ease",
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: BAR_PAD_Y,
                      height: BAR_HEIGHT,
                      zIndex: 2,
                      overflow: "visible",
                      pointerEvents: "none",
                      "& > *": { pointerEvents: "auto" },
                    }}
                  >
                    {!noGoalsData && (
                      <SummaryBarMarker
                        id="benchmark"
                        label="Benchmark:"
                        valueText={`${safeBenchmark} Referrals`}
                        tooltipText={TOOLTIP_BENCHMARK}
                        tooltipAriaLabel="Info about benchmark"
                        leftPercent={benchmarkPct}
                        position="above"
                        align={benchAlign}
                        hoveredId={hoveredId}
                        setHoveredId={setHoveredId}
                      />
                    )}
                    {!noGoalsData && (
                      <SummaryBarMarker
                        id="goal"
                        label="Goal:"
                        valueText={`${safeGoal} Referrals`}
                        tooltipText={TOOLTIP_GOAL}
                        tooltipAriaLabel="Info about goal"
                        leftPercent={100}
                        position="above"
                        anchor="fillEnd"
                        align="right"
                        hoveredId={hoveredId}
                        setHoveredId={setHoveredId}
                      />
                    )}
                    {!noGoalsData && (
                      <SummaryBarMarker
                        id="progress"
                        label="Progress:"
                        valueText={`${safeProgress} Referrals`}
                        tooltipText={TOOLTIP_PROGRESS}
                        tooltipAriaLabel="Info about progress"
                        leftPercent={progressPct}
                        position="below"
                        align={alignForPercent(progressPct)}
                        hoveredId={hoveredId}
                        setHoveredId={setHoveredId}
                      />
                    )}
                  </Box>

                  {!noGoalsData && safeGoal > 0 && (
                    <Box
                      aria-hidden
                      sx={{
                        position: "absolute",
                        left: `${benchmarkPct}%`,
                        top: BAR_PAD_Y - 2,
                        height: BAR_HEIGHT + 4,
                        width: "2px",
                        bgcolor: BENCHMARK_LINE,
                        transform: "translateX(-50%)",
                        borderRadius: 1,
                        zIndex: 1,
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </Box>

                {noGoalsData && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ mt: 2 }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    <Typography variant="body2" color="text.secondary">
                      Goal data is not available for this product yet.
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="contained"
                  onClick={onTrackStarts}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "4px",
                    lineHeight: 1,
                    px: 3,
                    py: 1.25,
                    minWidth: 200,
                    bgcolor: "#D96B04",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#C2410C", boxShadow: "none" },
                  }}
                >
                  {buttonText}
                </Button>
              </Box>
            </Stack>
            {!noGoalsData && estimatedHitGoal !== -1 && (
              <Typography
                sx={{
                  textAlign: "center",
                  p: "8px 10px",
                  fontSize: "14px",
                  weight: 400,
                  fontFamily: '"Helvetica Neue", Arial, sans-serif',
                }}
              >
                Referrals needed to hit goal:
                <Typography
                  component="span"
                  sx={{ fontWeight: 700, fontSize: "14px", ml: 1 }}
                >
                  {estimatedHitGoal}
                </Typography>
              </Typography>
            )}
            {goalsDataDisclaimerShow && <GoalsDataDisclaimer />}
          </>
        )}
      </CardContent>
    </Card>
  );
}
